import { create } from 'zustand';
import type { Study, WhereBean } from '../api/types';

/** Grid sort entry — matches MUI DataGrid GridSortItem shape */
export interface SortItem {
  field: string;
  sort: 'asc' | 'desc' | null | undefined;
}

/** A dynamic tab that can be opened (admin, viewer, etc.) */
export interface DynamicTab {
  id: string;
  label: string;
  type: 'worklist' | 'admin' | 'viewer' | 'messaging' | 'scheduling' | 'patients' | 'reports';
  /** For viewer tabs — the study being viewed */
  study?: Study;
  /** Admin panel type */
  adminPanel?: string;
  /** Which viewer provider to use (default: system default) */
  viewerId?: string;
  closeable?: boolean;
}

interface UIState {
  /** Currently selected study UIDs */
  selectedStudyUids: string[];
  /** Currently selected study objects (for detail panel) */
  selectedStudies: Study[];
  /** Active tab identifier */
  activeTab: string;
  /** Whether detail panel is visible */
  detailPanelOpen: boolean;
  /** Whether aggregation panel is visible */
  aggregationPanelOpen: boolean;
  /** Current page (0-indexed) */
  page: number;
  /** Active aggregation filter selections */
  activeFilters: Record<string, string[]>;
  /** Quick search text */
  quickSearch: string;
  /** Quick search field type */
  quickSearchField: string;
  /** Dynamic tabs (admin, viewer, etc.) */
  dynamicTabs: DynamicTab[];
  /** Dialog open states */
  advancedSearchOpen: boolean;
  preferencesOpen: boolean;
  savedSearchOpen: boolean;
  /** Advanced search where-clause filters */
  advancedFilters: WhereBean[];
  /** Sort model for the worklist DataGrid */
  sortModel: SortItem[];
  /** Optional Roentgen-driven cap on visible worklist results */
  resultLimit: number | null;
  /** Split-screen mode — show worklist + viewer side-by-side */
  splitScreen: boolean;
  /** Tab ID pinned in the viewer pane during split-screen */
  pinnedViewerTabId: string | null;

  setSelectedStudyUids: (uids: string[]) => void;
  setSelectedStudies: (studies: Study[]) => void;
  setActiveTab: (tab: string) => void;
  toggleDetailPanel: () => void;
  toggleAggregationPanel: () => void;
  setPage: (page: number) => void;
  setFilter: (field: string, values: string[]) => void;
  clearFilters: () => void;
  setQuickSearch: (text: string, field: string) => void;
  /** Set advanced search filters (WhereBean[]) */
  setAdvancedFilters: (filters: WhereBean[]) => void;
  /** Open a dynamic tab — if it already exists, activate it */
  openDynamicTab: (tab: DynamicTab) => void;
  /** Close a dynamic tab */
  closeDynamicTab: (tabId: string) => void;
  /** Sort model setter */
  setSortModel: (model: SortItem[]) => void;
  /** Set an optional visible result cap for the worklist */
  setResultLimit: (limit: number | null) => void;
  /** Dialog toggles */
  setAdvancedSearchOpen: (open: boolean) => void;
  setPreferencesOpen: (open: boolean) => void;
  setSavedSearchOpen: (open: boolean) => void;
  /** Toggle split-screen mode */
  toggleSplitScreen: () => void;
  /** Pin a specific viewer tab in the split pane */
  setPinnedViewerTab: (tabId: string | null) => void;
}

export const useUIStore = create<UIState>((set) => ({
  selectedStudyUids: [],
  selectedStudies: [],
  activeTab: 'studies',
  detailPanelOpen: true,
  aggregationPanelOpen: true,
  page: 0,
  activeFilters: {},
  quickSearch: '',
  quickSearchField: 'smart',
  dynamicTabs: [
    { id: 'admin-sites', label: 'Sites', type: 'admin', adminPanel: 'sites', closeable: true },
  ],
  advancedSearchOpen: false,
  preferencesOpen: false,
  savedSearchOpen: false,
  advancedFilters: [],
  sortModel: [],
  resultLimit: null,
  splitScreen: false,
  pinnedViewerTabId: null,

  setSelectedStudyUids: (uids) => set({ selectedStudyUids: uids }),
  setSelectedStudies: (studies) => set({ selectedStudies: studies }),
  setActiveTab: (tab) =>
    set((s) => {
      // In split mode, clicking a viewer tab should swap the pinned viewer
      if (s.splitScreen) {
        const dTab = s.dynamicTabs.find((t) => t.id === tab);
        if (dTab?.type === 'viewer') {
          return { pinnedViewerTabId: tab, page: 0 };
        }
      }
      return { activeTab: tab, page: 0 };
    }),
  toggleDetailPanel: () =>
    set((s) => ({ detailPanelOpen: !s.detailPanelOpen })),
  toggleAggregationPanel: () =>
    set((s) => ({ aggregationPanelOpen: !s.aggregationPanelOpen })),
  setPage: (page) => set({ page }),
  setFilter: (field, values) =>
    set((s) => ({
      activeFilters: { ...s.activeFilters, [field]: values },
      page: 0, // Reset page on filter change
    })),
  clearFilters: () => set({ activeFilters: {}, page: 0 }),
  setQuickSearch: (text, field) => set({ quickSearch: text, quickSearchField: field, page: 0 }),

  openDynamicTab: (tab) =>
    set((s) => {
      const exists = s.dynamicTabs.find((t) => t.id === tab.id);
      // Split-screen: viewer tabs pin to the viewer pane instead of switching active tab
      if (s.splitScreen && tab.type === 'viewer') {
        if (exists) {
          return { pinnedViewerTabId: tab.id };
        }
        return {
          dynamicTabs: [...s.dynamicTabs, tab],
          pinnedViewerTabId: tab.id,
        };
      }
      if (exists) {
        return { activeTab: tab.id };
      }
      return {
        dynamicTabs: [...s.dynamicTabs, tab],
        activeTab: tab.id,
      };
    }),

  closeDynamicTab: (tabId) =>
    set((s) => {
      const newTabs = s.dynamicTabs.filter((t) => t.id !== tabId);
      // If closing the active tab, switch to first worklist tab
      const newActive = s.activeTab === tabId ? 'studies' : s.activeTab;
      // If closing the pinned viewer, find another viewer or clear
      let newPinned = s.pinnedViewerTabId;
      if (s.pinnedViewerTabId === tabId) {
        const nextViewer = newTabs.find((t) => t.type === 'viewer');
        newPinned = nextViewer?.id ?? null;
      }
      return { dynamicTabs: newTabs, activeTab: newActive, pinnedViewerTabId: newPinned };
    }),

  setSortModel: (model) => set({ sortModel: model }),
  setResultLimit: (limit) => set({ resultLimit: limit, page: 0 }),
  setAdvancedSearchOpen: (open) => set({ advancedSearchOpen: open }),
  setPreferencesOpen: (open) => set({ preferencesOpen: open }),
  setSavedSearchOpen: (open) => set({ savedSearchOpen: open }),
  setAdvancedFilters: (filters) => set({ advancedFilters: filters, page: 0 }),
  toggleSplitScreen: () =>
    set((s) => {
      const next = !s.splitScreen;
      if (!next) {
        // Exiting split — clear pinned viewer
        return { splitScreen: false, pinnedViewerTabId: null };
      }
      // Entering split — if a viewer tab is active, pin it and switch back to worklist
      const viewerTab = s.dynamicTabs.find((t) => t.type === 'viewer' && t.id === s.activeTab);
      if (viewerTab) {
        return { splitScreen: true, pinnedViewerTabId: viewerTab.id, activeTab: 'studies' };
      }
      // Entering split with no active viewer — just enable, first viewer opened will pin
      return { splitScreen: true };
    }),
  setPinnedViewerTab: (tabId) => set({ pinnedViewerTabId: tabId }),
}));
